import { isNil } from 'lodash';
import moment from 'moment';
import {
  isDateComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { notNil } from '@shared/util/notNil';
import { v4 as uuidv4 } from '@shared/util/uuid';
import {
  ControlsState,
  FilterOption,
  FilterSelectionState,
  SortOption,
  SortSelectionState,
} from '../types';

export function mapCollectionCriteriaToState(
  collectionCriteria: MBAPI.ViewTemplate_collectionCriteria | null,
  availableFilters: FilterOption[],
  availableSorts: SortOption[],
  availableGroups: FilterOption[],
): ControlsState {
  // TODO
  // omniSearchQuery if saved

  const filters = mapFilterByComponentToState(
    collectionCriteria?.filterByComponents ?? [],
    availableFilters,
  );

  const sorts = mapSortByComponentToState(
    collectionCriteria?.sortByComponents ?? [],
    availableSorts,
  );

  const groups = mapGroupsToState(collectionCriteria?.groups ?? [], availableGroups);

  return {
    filters,
    availableFilters,
    selectedFilters: Array.from(filters.values())
      .map(filter => filter.filter)
      .filter(notNil),

    sorts,
    availableSorts,
    selectedSorts: Array.from(sorts.values())
      .map(sort => sort.sort)
      .filter(notNil),

    groups,
    availableGroups,
    selectedGroups: groups.map(g =>
      Array.from(g.values())
        .map(group => group.filter)
        .filter(notNil),
    ),

    search: null,
  };
}

export function mapFilterByComponentToState(
  filterByComponents: MBAPI.FilterByComponent[],
  availableFilters: FilterOption[],
): Map<string, FilterSelectionState> {
  const filters = new Map<string, FilterSelectionState>();
  filterByComponents?.forEach(filter => {
    const option = availableFilters.find(
      filterOption => filterOption.component?.id === filter.componentId,
    );
    if (!isNil(option) && !isNil(option.component)) {
      const uuid = option.uuid ?? uuidv4();
      let selection: any = null;
      let negate = false;
      if (isTagComponent(option.component)) {
        if (!isNil(filter.value.containsString?.values)) {
          selection = filter.value.containsString.values.map(value => {
            return {
              filter: { uuid, ...option },
              ...((
                option.component as MBAPI.WorkflowTemplate_components_TagComponent
              )?.options?.find(o => o.value === value) || {}),
            };
          });
          negate = !!filter.value.containsString.negate;
        } else {
          selection = [
            {
              filter: { uuid, ...option },
              label: `No ${option.component.label}`,
              value: 'empty',
            },
          ];
        }
      }
      if (isRelatedCardComponent(option.component)) {
        if (!isNil(filter.value.containsString?.values)) {
          selection = filter.value.containsString.values.map(value => {
            return {
              filter: { uuid, ...option },
              value,
            };
          });
        } else {
          selection = [
            {
              filter: { uuid, ...option },
              label: `No ${option.component.label}`,
              value: 'empty',
            },
          ];
        }
      }
      if (isPersonComponent(option.component)) {
        if (!isNil(filter.value.containsNumber?.values)) {
          selection = filter.value.containsNumber.values.map(value => {
            return {
              filter: { uuid, ...option },
              value,
            };
          });
        } else if (!isNil(filter.value.containsCurrentUser)) {
          selection = [
            {
              filter: { uuid, ...option },
              value: 'currentUser',
            },
          ];
        } else {
          selection = [
            {
              filter: { uuid, ...option },
              label: `No ${option.component.label}`,
              value: 'empty',
            },
          ];
        }
      }
      if (isTextComponent(option.component)) {
        selection = filter.value.isEmpty
          ? {
              operator: 'empty',
            }
          : {
              operator: 'contains',
              value: filter.value.stringWildcard?.value,
            };
      }
      if (isDateComponent(option.component)) {
        if (filter.value.isEmpty) {
          selection = { operator: 'empty', after: null, before: null };
        } else if (!isNil(filter.value.dateIs)) {
          // TODO making assumptions about 0 here since API data
          // doesn't indicate if 'before' or 'after' today
          // were selected in the UI. Behavior
          // is equivalent with 0 duration, but that might change
          // if we change today to be start of day.
          const relativeDate = filter.value.dateIs.relativeDate!;
          if (
            relativeDate.duration < 0 ||
            (relativeDate.duration === 0 && ['GT', 'GTE'].includes(filter.value.dateIs.operator))
          ) {
            selection = {
              operator: 'daysBeforeToday',
              comparisonOperator: filter.value.dateIs.operator,
              duration: relativeDate.duration,
            };
          } else {
            selection = {
              operator: 'daysAfterToday',
              comparisonOperator: filter.value.dateIs.operator,
              duration: relativeDate.duration,
            };
          }
        } else if (!isNil(filter.value.dateTokenIs)) {
          selection = { operator: 'today', after: null, before: null };
        } else if (
          !isNil(filter.value.dateRange?.after) &&
          !isNil(filter.value.dateRange?.before) &&
          moment(filter.value.dateRange?.after)
            .startOf('day')
            .toISOString() ===
            moment(filter.value.dateRange?.before)
              .startOf('day')
              .toISOString()
        ) {
          selection = { operator: 'exactly', ...filter.value.dateRange };
        } else if (!isNil(filter.value.dateRange?.after) && isNil(filter.value.dateRange?.before)) {
          selection = { operator: 'startsOn', ...filter.value.dateRange };
        } else if (isNil(filter.value.dateRange?.after) && !isNil(filter.value.dateRange?.before)) {
          selection = { operator: 'endsOn', ...filter.value.dateRange };
        } else {
          selection = { operator: 'between', ...filter.value.dateRange };
        }
      }
      if (isNumberComponent(option.component)) {
        selection = filter.value.isEmpty?.value ? { operator: 'empty' } : filter.value.numberIs;
      }

      filters.set(uuid, {
        filter: { uuid, ...option },
        selection,
        negate,
      });
    }
  });

  return filters;
}

function mapSortByComponentToState(
  sortByComponents: MBAPI.SortByComponent[],
  availableSorts: SortOption[],
): Map<string, SortSelectionState> {
  const sorts = new Map<string, SortSelectionState>();
  sortByComponents?.forEach(sort => {
    const option = availableSorts.find(sortOption => sortOption.component?.id === sort.componentId);
    if (!isNil(option)) {
      const uuid = uuidv4();
      const direction: MBAPI.SortDirection = sort.direction === 'ASC' ? 'ASC' : 'DESC';
      sorts.set(uuid, {
        sort: { uuid, ...option, direction },
        direction,
      });
    }
  });
  return sorts;
}

function mapGroupsToState(groups: MBAPI.CollectionGroup[], availableGroups: FilterOption[]) {
  return groups.map(group => {
    const groupState = mapFilterByComponentToState(group.filterByComponents ?? [], availableGroups);
    const groupMap = new Map<string, FilterSelectionState>();
    groupState.forEach(state => {
      if (!isNil(state.filter?.uuid)) {
        groupMap.set(state.filter?.uuid, state);
      }
    });
    return groupMap;
  });
}
